/* eslint-disable no-restricted-globals */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ShipeeziNewGrid, { Category, Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import OutlinedRouting from "../../../../assets/icons/OutlinedRouting";
import { Color } from "../../../../components/Form/Button";
import RowActionRoutePlanner from "../../../../components/RowActionRoutePlanner";
import TitlePage from "../../../../components/TitlePage";
import { useAppState } from "../../../../context/StateContext";
import { getAppLayout } from "../../../../helpers/helpers";
import { Despatch } from "../../../../models/despatch";
import { Location } from "../../../../models/location";
import { Filters, Searches } from "../../../../models/shipeezi.table";
import { DespatchStatus } from "../../../../util/enums";
import { formatDateWithoutTimeZone } from "../../../../util/Format";
import { DespatchEmptySection } from "./components/EmptySection";
import LocationRow from "./components/LocationRow";
import PrintDeliveryDocketButton from "./components/PrintDeliveryDocketButton";
import DespatchBadgeStatus from "./components/StatusBadge";
import useAction from "./hooks/useAction";
import useRoutePlannerAction from "./hooks/useRoutePlannerActions";
import useStyles, { StyledButton } from "./styles";
import DespatchSideView, { DespatchViewandles } from "./View";

const noDocumentsIcon = require("Assets/img/no-documents.svg");

const Despatches = () => {
  const history = useHistory();
  const sideViewRef = useRef<DespatchViewandles>(null);
  const { t: translate } = useTranslation();
  const { getAllDespatches, usePrintDeliveryDocket } = useAction();
  const {
    useAddToRoutePlanner,
    useRemoveFromRoutePlanner,
    useAddListToRoutePlanner,
    useRemoveListFromRoutePlanner,
  } = useRoutePlannerAction();

  const { currentUser } = useAppState();

  const {
    mutate: printDeliveryDocket,
    isLoading: loadingDeliveryDocket,
    variables: deliveryDocketId,
  } = usePrintDeliveryDocket();

  const {
    mutate: addToRoutePlanner,
    isLoading: addingToRoutePlanner,
    variables: taskToAdd,
  } = useAddToRoutePlanner();

  const {
    mutate: removeFromRoutePlanner,
    isLoading: removingFromRoutePlanner,
    variables: taskIdToRemove,
  } = useRemoveFromRoutePlanner();

  const { mutate: addListToRoutePlanner, isLoading: addingListToRoutePlanner } =
    useAddListToRoutePlanner();

  const { mutate: removeListFromRoutePlanner, isLoading: removingListFromRoutePlanner } =
    useRemoveListFromRoutePlanner();

  const classes = useStyles();

  const [despatches, setDespatches] = useState<Despatch[]>([]);
  const [totalItems, setTotalItems] = useState<number>(100);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>({});
  const [selectedItems, setSelectedItems] = useState<number[]>([]);

  const selectedDespatches = useMemo(
    () => selectedItems.map((value) => despatches.find((despatch) => despatch.id === value)),
    [selectedItems]
  );

  const { data, isLoading, isRefetching } = getAllDespatches(page, pageSize, searches, filters);

  useEffect(() => {
    if (data && data.rows) {
      setDespatches(data.rows);
      setTotalItems(data.count);
    }
  }, [data]);

  const columns: Column[] = [
    { name: "id", title: `${translate("despatch")} #` },
    {
      name: "createdAt",
      title: `${translate("despatchDate")}`,
      formatText: (value: string) => formatDateWithoutTimeZone(value),
      hideSearch: true,
    },
    {
      name: "originAddress",
      title: `${translate("senderAddress")}`,
      formatComponent: (address) => <LocationRow location={address} />,
      searchIdentifier: "originAddress.longAddress",
    },
    {
      name: "destinationAddress",
      title: `${translate("deliveryAddress")}`,
      formatComponent: (address) => <LocationRow location={address} />,
      searchIdentifier: "destinationAddress.longAddress",
    },
    {
      name: "originAddress",
      title: `${translate("receiver")}`,
      formatText: (address: Location) => address?.name || "",
      searchIdentifier: "originAddress.name",
    },
    {
      name: "status",
      title: `${translate("status")}`,
      formatComponent: (status: DespatchStatus) => status && <DespatchBadgeStatus value={status} />,
      hideSearch: true,
    },
  ];

  const editDispatch = (despatchId: any) => {
    if (despatchId) {
      history.push({
        pathname: `despatches-mnt`,
        state: { despatchId, viewOnly: false },
      });
    }
  };

  const viewDispatch = (despatchId: number | string) => {
    if (despatchId && !Number.isNaN(Number(despatchId)))
      sideViewRef.current?.open(Number(despatchId));
  };

  const handleChangeCurrentPage = (currentPage: any) => {
    setPage(currentPage - 1);
  };

  const handleChangeRowsPerPage = async (newPageSize: any) => {
    setPageSize(newPageSize);
  };

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleAddToRoutePlanner = (despatch?: Despatch) => {
    if (!despatch) return;

    addToRoutePlanner(despatch);
  };

  const handleRemoveFromRoutePlanner = (id?: number) => {
    if (!id) return;

    removeFromRoutePlanner(id);
  };

  const handleAddListToRoutePlanner = () => {
    if (!selectedDespatches?.length) return;

    addListToRoutePlanner(selectedDespatches as Despatch[]);
  };

  const handleRemoveListFromRoutePlanner = () => {
    if (!selectedDespatches?.length) return;

    removeListFromRoutePlanner(selectedDespatches as Despatch[]);
  };

  const renderRoutePlannerAction = (index: number) => {
    const despatchToUpdate = despatches.find((despatch) => Number(despatch.id) === Number(index));

    return (
      <RowActionRoutePlanner
        uuid={despatchToUpdate?.externalUuid}
        loading={
          (taskToAdd?.id === despatchToUpdate?.id && addingToRoutePlanner) ||
          (taskIdToRemove === despatchToUpdate?.id && removingFromRoutePlanner)
        }
        add={() => handleAddToRoutePlanner(despatchToUpdate)}
        remove={() => handleRemoveFromRoutePlanner(despatchToUpdate?.id)}
      />
    );
  };

  const filterCategories: Category[] = [
    {
      name: translate("status"),
      column: "status",
      options: [
        { value: DespatchStatus.PLANNED, label: translate("planned") },
        { value: DespatchStatus.BOOKED, label: translate("booked") },
        { value: DespatchStatus.STAGED, label: translate("staged") },
        { value: DespatchStatus.LOADED, label: translate("loaded") },
        { value: DespatchStatus.IN_TRANSIT, label: translate("inTransit") },
        { value: DespatchStatus.DELIVERED, label: translate("delivered") },
      ],
    },
  ];

  const openCreateDespatch = () => {
    history.push({
      pathname: `/${getAppLayout(location)}/despatches-mnt`,
      state: { viewOnly: false },
    });
  };

  const openRoutePlannerPage = () => {
    history.push({
      pathname: `/${getAppLayout(location)}/route-planner`,
    });
  };

  const renderRoutePlanner = useCallback(() => {
    return (
      <StyledButton customColor={Color.SECONDARY_OUTLINED} onClick={openRoutePlannerPage}>
        <OutlinedRouting className={classes.marginIcon} />
        {translate("routePlanner")}
      </StyledButton>
    );
  }, []);

  const renderManageRoutePlannerButtons = useCallback(() => {
    const despatchesAlreadyIncluded = selectedDespatches.every(
      (despatch) => despatch?.externalUuid
    );
    const despatchesNotIncluded = selectedDespatches.every((despatch) => !despatch?.externalUuid);

    if (despatchesAlreadyIncluded) {
      return (
        <StyledButton
          customColor={Color.SECONDARY_OUTLINED}
          loading={removingListFromRoutePlanner}
          onClick={handleRemoveListFromRoutePlanner}
        >
          <RowActionRoutePlanner.RemoveIcon size={18} className={classes.marginIcon} />
          {translate("removeDespatchesToRoute")}
        </StyledButton>
      );
    }

    if (despatchesNotIncluded) {
      return (
        <StyledButton
          customColor={Color.SECONDARY_OUTLINED}
          loading={addingListToRoutePlanner}
          onClick={handleAddListToRoutePlanner}
        >
          <RowActionRoutePlanner.AddIcon size={18} className={classes.marginIcon} />
          {translate("addDespatchesToRoute")}
        </StyledButton>
      );
    }
  }, [selectedDespatches]);

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: [
        ...(selectedItems?.length && currentUser?.company.useRoutePlanner
          ? ["onManageRoutePlanner"]
          : [
              ...(currentUser?.company.useRoutePlanner ? ["onOpenRoutePlanner"] : []),
              "handleNewRecord",
            ]),
      ],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: filterCategories,
      onChange: handleFilter,
    },
    onManageRoutePlanner: renderManageRoutePlannerButtons(),
    onOpenRoutePlanner: renderRoutePlanner(),
    handleNewRecord: {
      text: translate("createDespatch"),
      action: openCreateDespatch,
      tooltip: translate("createDespatch"),
    },
  };

  const renderPrintDeliveryDocketButton = (id: number) => {
    return (
      <PrintDeliveryDocketButton
        id={id}
        loading={loadingDeliveryDocket && deliveryDocketId === id}
        handleOnClick={printDeliveryDocket}
      />
    );
  };

  const renderScreen = () => {
    if (
      !isLoading &&
      !searches.length &&
      !Object.keys(filters).length &&
      (!data || !data?.rows?.length)
    ) {
      return (
        <div className={classes.dispatchesGridWrapper}>
          <DespatchEmptySection
            buttonLabel={translate("createDespatch")}
            descriptionScreen={translate("despatch")?.toLowerCase()}
            onAdd={openCreateDespatch}
            icon={noDocumentsIcon}
          />
        </div>
      );
    }

    return (
      <div className={classes.dispatchesGridWrapper} data-cy="dispatchesListGrid">
        <ShipeeziNewGrid
          data={despatches}
          totalItems={totalItems}
          loadingData={isLoading || isRefetching}
          namePage={translate("despatches")}
          emptyData={
            isLoading ? translate("loadingDispatches") : translate("noInformationToBeShown")
          }
          columns={columns}
          currentPage={page}
          pageSize={pageSize}
          pageSizeList={totalItems > pageSize ? [20, 40, 60, 100] : []}
          onChangePageSize={handleChangeRowsPerPage}
          onNextPage={handleChangeCurrentPage}
          onPreviousPage={handleChangeCurrentPage}
          onGoToPage={handleChangeCurrentPage}
          generalActions={generalActions}
          rowActions={{
            handleEdit: (despatchId) => editDispatch(despatchId),
            handleDetails: (despatchId) => viewDispatch(despatchId),
          }}
          rowActionsExtra={[
            {
              title: translate("print"),
              key: "print-delivery-docket",
              buttonAndAction: renderPrintDeliveryDocketButton,
            },
            ...(currentUser?.company?.useRoutePlanner
              ? [
                  {
                    title: translate("routePlanner"),
                    key: "handleRoutePlanner",
                    buttonAndAction: renderRoutePlannerAction,
                  },
                ]
              : []),
          ]}
          positionActionsColumn="right"
          selecteds={selectedItems}
          handleSelect={setSelectedItems}
          showSelection={totalItems !== 0}
          showFooterGrid={totalItems !== 0}
        />
      </div>
    );
  };

  return (
    <>
      <div data-cy="despatches-grid">
        <TitlePage title={translate("despatches")} />
        {renderScreen()}
        <DespatchSideView ref={sideViewRef} />
      </div>
    </>
  );
};

export default Despatches;
